/**
 * Hot Returns Alert Panel - Phase 1
 * Shows dealer alerts for returning shoppers
 * ADDITIVE component - does not modify unlock feature
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Bell, Clock, Car, User, CheckCircle, X, ChevronRight, RefreshCw } from 'lucide-react';

// Resolve backend URL via window.location.origin so this works for
// preview, custom prod domain, and default emergent.host alike.
const getBackendUrl = () => {
  if (typeof window !== 'undefined' && window.location && window.location.origin) {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};
const API_URL = getBackendUrl();

/**
 * Format vehicle identifier for display
 * Converts internal prefixes to user-friendly labels
 */
const formatVehicleId = (rawId) => {
  if (!rawId) return null;
  
  if (rawId.startsWith('VEHICLE_ID:')) {
    return `Stock #${rawId.replace('VEHICLE_ID:', '')}`;
  }
  if (rawId.startsWith('ID:')) {
    return `#${rawId.replace('ID:', '')}`;
  }
  if (rawId.startsWith('PATH:')) {
    return null; // Don't show path-based IDs in alerts
  }
  if (rawId.startsWith('UNKNOWN:')) {
    return null;
  }
  
  // Valid VIN - show last 6 chars
  if (/^[A-HJ-NPR-Z0-9]{17}$/i.test(rawId)) {
    return `VIN: ...${rawId.slice(-6)}`;
  }
  
  return `#${rawId}`;
};

export const HotReturnsPanel = ({ token, onAlertClick }) => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchAlerts = useCallback(async () => {
    if (!token) return;

    // iter82 — Bug 5: 10-second AbortController timeout so a hung
    // fetch can never strand the UI in "loading" forever. The
    // finally block always runs, clearing the spinner and surfacing
    // an error/empty state instead.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/dashboard/alerts?limit=20&unread_only=false`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error('Failed to fetch alerts');
      }

      const data = await response.json();
      setAlerts(data.alerts || []);
      setUnreadCount(data.unread_count || 0);
      setError(null);
    } catch (err) {
      // AbortError fires when our timeout trips; surface as a
      // friendly message instead of the cryptic DOMException one.
      const msg = err.name === 'AbortError'
        ? 'Request timed out. Try again in a moment.'
        : err.message;
      console.error('Error fetching alerts:', err);
      setError(msg);
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchAlerts();
    // Refresh every 60 seconds
    const interval = setInterval(fetchAlerts, 60000);
    return () => clearInterval(interval);
  }, [fetchAlerts]);

  const markAsRead = async (alertId) => {
    try {
      await fetch(`${API_URL}/api/dashboard/alerts/${alertId}/read`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      setAlerts(prev => prev.map(a => 
        a.id === alertId ? { ...a, read_at: new Date().toISOString() } : a
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Error marking alert as read:', err);
    }
  };

  const dismissAlert = async (alertId, e) => {
    e.stopPropagation();
    try {
      await fetch(`${API_URL}/api/dashboard/alerts/${alertId}/dismiss`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      setAlerts(prev => prev.filter(a => a.id !== alertId));
    } catch (err) {
      console.error('Error dismissing alert:', err);
    }
  };

  const markAllRead = async () => {
    try {
      await fetch(`${API_URL}/api/dashboard/alerts/read-all`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      setAlerts(prev => prev.map(a => ({ ...a, read_at: new Date().toISOString() })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  };

  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'border-l-red-500 bg-red-50';
      case 'medium': return 'border-l-yellow-500 bg-yellow-50';
      case 'low': return 'border-l-gray-400 bg-gray-50';
      default: return 'border-l-blue-500 bg-blue-50';
    }
  };

  if (loading && alerts.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6" data-testid="hot-returns-panel">
        <div className="flex items-center justify-center h-32">
          <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow" data-testid="hot-returns-panel">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-orange-100 rounded-lg">
            <Bell className="w-5 h-5 text-orange-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Hot Returns</h3>
            <p className="text-sm text-gray-500">
              {unreadCount > 0 ? `${unreadCount} new alerts` : 'No new alerts'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <button
              onClick={markAllRead}
              className="text-sm text-blue-600 hover:text-blue-700"
              data-testid="mark-all-read-btn"
            >
              Mark all read
            </button>
          )}
          <button
            onClick={fetchAlerts}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            data-testid="refresh-alerts-btn"
          >
            <RefreshCw className={`w-4 h-4 text-gray-500 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Alerts List */}
      <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
        {error && (
          <div className="p-4 text-sm text-red-600 bg-red-50">
            Error loading alerts: {error}
          </div>
        )}
        
        {!error && alerts.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            <Bell className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No alerts yet</p>
            <p className="text-sm mt-1">You&apos;ll see alerts when unlocked shoppers return</p>
          </div>
        )}

        {alerts.map((alert) => (
          <div
            key={alert.id}
            onClick={() => {
              if (!alert.read_at) markAsRead(alert.id);
              if (onAlertClick) onAlertClick(alert);
            }}
            className={`p-4 border-l-4 cursor-pointer hover:bg-gray-50 transition-colors ${getPriorityColor(alert.priority)} ${!alert.read_at ? 'font-medium' : ''}`}
            data-testid={`alert-item-${alert.id}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  {alert.alert_type === 'hot_return' && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                      🔥 Hot Return
                    </span>
                  )}
                  {/* Show relative time for return - emphasize recent activity */}
                  {alert.last_activity_at && !alert.read_at && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                      Returned {formatTimeAgo(alert.last_activity_at)}
                    </span>
                  )}
                  {(!alert.last_activity_at || alert.read_at) && (
                    <span className="text-xs text-gray-500">{formatTimeAgo(alert.created_at)}</span>
                  )}
                </div>
                <p className="text-sm text-gray-900 truncate">{alert.title}</p>
                {alert.message && (
                  <p className="text-sm text-gray-600 mt-1 line-clamp-2">{alert.message}</p>
                )}
                {alert.vin && formatVehicleId(alert.vin) && (
                  <div className="flex items-center gap-1 mt-2 text-xs text-gray-500">
                    <Car className="w-3 h-3" />
                    <span>{formatVehicleId(alert.vin)}</span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1">
                {!alert.read_at && (
                  <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                )}
                <button
                  onClick={(e) => dismissAlert(alert.id, e)}
                  className="p-1 hover:bg-gray-200 rounded transition-colors"
                  data-testid={`dismiss-alert-${alert.id}`}
                >
                  <X className="w-4 h-4 text-gray-400" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      {alerts.length > 0 && (
        <div className="px-6 py-3 border-t border-gray-200 bg-gray-50">
          <button
            className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
            data-testid="view-all-alerts-btn"
          >
            View all alerts
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default HotReturnsPanel;
